import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { NuqsAdapter } from 'nuqs/adapters/react'
import LoginPage from '@/app/pages/LoginPage'
import MainLayout from '@/app/components/layout/MainLayout'
import ProtectedRoute from './ProtectedRoute'
import RoleProtectedRoute from './RoleProtectedRoute'
import ClientManagerLayout from '@/app/components/layout/ClientManagerLayout'
import DashboardPage from '@/app/pages/dashboard/page'
import OrganizationsPage from '@/app/pages/OrganizationsPage'
import SitesPage from '@/app/pages/SitesPage'
import PartyPage from '@/app/pages/party/page'
import ItemsPage from '@/app/pages/items/page'

// Import Settings Pages
import UnitsPage from '@/app/pages/settings/units/page'
import HsnCodesPage from '@/app/pages/settings/hsn-codes/page'
import TaxRatesPage from '@/app/pages/settings/tax-rates/page'
import CategoriesPage from '@/app/pages/settings/categories/page'
import CategoryDetailPage from '@/app/pages/settings/categories/detail/page'
import OrganizationSettingsPage from '@/app/pages/settings/organization-settings/page'
import ComplianceServicesPage from '@/app/pages/settings/compliance-services/page'
import ReturnGenerationPage from '@/app/pages/settings/return-generation/page'
import TeamsPage from '@/app/pages/settings/teams/page'

// Import Accounting Pages
import ChartOfAccountsPage from '@/app/pages/accounting/chart-of-accounts/page'
import AccountStatementPage from '@/app/pages/accounting/account-statement/page'

// Import Inventory Pages
import InventoryPage from '@/app/pages/inventory/page'

// Import Pricing Page
import PricingPage from '@/app/pages/pricing/page'
import BulkPriceUpdatePage from '@/app/pages/pricing/bulk-update/page'
import SiteAvailabilityPage from '@/app/pages/pricing/site-availability/page'
import VoucherTypesPage from '@/app/pages/transactions/voucher-types/page'
import VouchersPage from '@/app/pages/transactions/vouchers/page'

// Import Sales Order Pages
import SalesOrdersPage from '@/app/pages/sales-orders/list/page'
import SalesOrderDetailPage from '@/app/pages/sales-orders/detail/page'

// Import Sales Invoice Pages
import SalesInvoicesPage from '@/app/pages/sales-invoices/list/page'
import SalesInvoiceDetailPage from '@/app/pages/sales-invoices/detail/page'

// Import Quotation Pages
import QuotationsPage from '@/app/pages/quotations/list/page'
import QuotationDetailPage from '@/app/pages/quotations/detail/page'

// Import Expense Pages
import ExpenseListPage from '@/app/pages/expenses/list/page'
import ExpenseDetailPage from '@/app/pages/expenses/detail/page'

// Import Payments Pages
import PaymentsInPage from '@/app/pages/payments-in/page'
import PaymentsInListPage from '@/app/pages/payments-in/list/page'
import PaymentsInDetailPage from '@/app/pages/payments-in/detail/page'
import PaymentsOutPage from '@/app/pages/payments-out/page'
import PaymentsOutListPage from '@/app/pages/payments-out/list/page'
import PaymentsOutDetailPage from '@/app/pages/payments-out/detail/page'

// Import Cheque Pages
import ChequesListPage from '@/app/pages/cheques/list/page'
import RecordChequePage from '@/app/pages/cheques/new/page'

// Import Purchase Invoice Pages
import PurchaseInvoicesPage from '@/app/pages/purchase-invoices/list/page'
import PurchaseInvoiceDetailPage from '@/app/pages/purchase-invoices/detail/page'

// Import Credit Note Pages
import CreditNotesPage from '@/app/pages/credit-notes/list/page'
import CreditNoteDetailPage from '@/app/pages/credit-notes/detail/page'

// Import Debit Note Pages
import DebitNotesPage from '@/app/pages/debit-notes/list/page'
import DebitNoteDetailPage from '@/app/pages/debit-notes/detail/page'

// Import Offline Invoice Pages
import OfflineInvoicesPage from '@/app/pages/offline-invoices/page'
import OfflineInvoiceListPage from '@/app/pages/offline-invoices/list/page'
import OfflineInvoiceCreatePage from '@/app/pages/offline-invoices/create/page'
import { default as ItemDetailPage } from '@/app/pages/items/detail/page'
import PartyDetailPage from '@/app/pages/party/detail/page'

// Import Day-End Closure Page
import DayEndClosurePage from '@/app/pages/day-end-closure/page'

// Import Reporting Pages
import PartiesReportPage from '@/app/pages/admin/reporting/parties/page'
import CustomerMetricsPage from '@/app/pages/admin/reporting/customers/page'
import SalesReportPage from '@/app/pages/admin/reporting/sales/page'

// Import Internal Transfer Pages
import InternalTransfersListPage from '@/app/pages/items/internal-transfers/list/page'
import InternalTransfersDetailPage from '@/app/pages/items/internal-transfers/detail/page'

// Import Low Stock Report Page
import LowStockReportPage from '@/app/pages/items/low-stock-report/page'

// Import Item Collections Pages
import ItemCollectionsListPage from '@/app/pages/items/collections/list/page'
import ItemCollectionDetailPage from '@/app/pages/items/collections/detail/page'

// Import Client Manager Pages
import ClientManagerDashboardPage from '@/app/pages/client-manager/dashboard/page'
import MyPartiesPage from '@/app/pages/client-manager/parties/page'
import TaskListPage from '@/app/pages/tasks/list/page'
import TaskDetailPage from '@/app/pages/tasks/detail/page'
import UnauthorizedPage from '@/app/pages/unauthorized/page'


const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <NuqsAdapter>
        <Routes>
          {/* Login Route */}
          <Route path="/login" element={<LoginPage />} />

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          {/* Admin/Standard User Routes - Role Protected */}
          <Route element={<RoleProtectedRoute allowedRoles={['superuser', 'org_admin', 'standard_user', 'client_manager']} />}>
            <Route element={<MainLayout />}>
              {/* Default route after login */}
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/organizations" element={<OrganizationsPage />} />
            <Route path="/sites" element={<SitesPage />} />
            <Route path="/party/detail/:id" element={<PartyDetailPage />} />
            <Route path="/party" element={<PartyPage />} />
            <Route path="/items" element={<ItemsPage />} />
            <Route path="/items/detail/:id" element={<ItemDetailPage />} />
            <Route path="/items/detail" element={<ItemDetailPage />} />
            
            {/* Internal Transfer Routes */}
            <Route path="/items/internal-transfers" element={<InternalTransfersListPage />} />
            <Route path="/items/internal-transfers/detail/:id" element={<InternalTransfersDetailPage />} />
            <Route path="/items/internal-transfers/detail" element={<InternalTransfersDetailPage />} />
            
            {/* Low Stock Report Route */}
            <Route path="/items/low-stock-report" element={<LowStockReportPage />} />

            {/* Item Collections Routes */}
            <Route path="/items/collections" element={<ItemCollectionsListPage />} />
            <Route path="/items/collections/detail/:id" element={<ItemCollectionDetailPage />} />
            <Route path="/items/collections/detail" element={<ItemCollectionDetailPage />} />

            {/* Settings Routes */}
            <Route path="/settings/units" element={<UnitsPage />} />
            <Route path="/settings/hsn-codes" element={<HsnCodesPage />} />
            <Route path="/settings/tax-rates" element={<TaxRatesPage />} />
            <Route path="/settings/categories" element={<CategoriesPage />} />
            <Route path="/settings/categories/detail/:id" element={<CategoryDetailPage />} />
            <Route path="/settings/organization-settings" element={<OrganizationSettingsPage />} />
            <Route path="/settings/compliance-services" element={<ComplianceServicesPage />} />
            <Route path="/settings/return-generation" element={<ReturnGenerationPage />} />
            <Route path="/settings/teams" element={<TeamsPage />} />

            {/* Accounting Routes */}
            <Route path="/accounting/chart-of-accounts" element={<ChartOfAccountsPage />} />
            <Route path="/accounting/account-statement" element={<AccountStatementPage />} />

            {/* Inventory Routes */}
            <Route path="/inventory" element={<InventoryPage />} />

            {/* Pricing Route */}
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/pricing/bulk-update" element={<BulkPriceUpdatePage />} />
            <Route path="/pricing/site-availability" element={<SiteAvailabilityPage />} />

            {/* Transactions Routes */}
            <Route path="/voucher-types" element={<VoucherTypesPage />} />
            <Route path="/vouchers" element={<VouchersPage />} />

            {/* Sales Order Routes - Keep detail/:id route before the detail route */}
            <Route path="/sales-orders/detail/:id" element={<SalesOrderDetailPage />} />
            <Route path="/sales-orders/detail" element={<SalesOrderDetailPage />} />
            <Route path="/sales-orders/list" element={<SalesOrdersPage />} />
            <Route path="/sales-orders" element={<Navigate to="/sales-orders/list" replace />} />

            {/* Sales Invoice Routes */}
            <Route path="/sales-invoices/detail/:id" element={<SalesInvoiceDetailPage />} />
            <Route path="/sales-invoices/detail" element={<SalesInvoiceDetailPage />} />
            <Route path="/sales-invoices/new" element={<SalesInvoiceDetailPage />} />
            <Route path="/sales-invoices/list" element={<SalesInvoicesPage />} />
            <Route
              path="/sales-invoices"
              element={<Navigate to="/sales-invoices/list" replace />}
            />

            {/* Quotation Routes */}
            <Route path="/quotations/detail/:id" element={<QuotationDetailPage />} />
            <Route path="/quotations/detail" element={<QuotationDetailPage />} />
            <Route path="/quotations/list" element={<QuotationsPage />} />
            <Route path="/quotations" element={<Navigate to="/quotations/list" replace />} />

            {/* Expense Routes */}
            <Route path="/expenses/list" element={<ExpenseListPage />} />
            <Route path="/expenses/detail" element={<ExpenseDetailPage />} />
            <Route path="/expenses/detail/:id" element={<ExpenseDetailPage />} />

            {/* Payments Routes */}
            <Route path="/payments-in" element={<PaymentsInPage />} />
            <Route path="/payments-in/list" element={<PaymentsInListPage />} />
            <Route path="/payments-in/detail/:id" element={<PaymentsInDetailPage />} />

            <Route path="/payments-out" element={<PaymentsOutPage />} />
            <Route path="/payments-out/list" element={<PaymentsOutListPage />} />
            <Route path="/payments-out/detail/:id" element={<PaymentsOutDetailPage />} />

            {/* Cheque Routes */}
            <Route path="/cheques/list" element={<ChequesListPage />} />
            <Route path="/cheques/new" element={<RecordChequePage />} />
            <Route path="/cheques" element={<Navigate to="/cheques/list" replace />} />

            {/* Purchase Invoice Routes */}
            <Route path="/purchase-invoices/detail/:id" element={<PurchaseInvoiceDetailPage />} />
            <Route path="/purchase-invoices/detail" element={<PurchaseInvoiceDetailPage />} />
            <Route path="/purchase-invoices/list" element={<PurchaseInvoicesPage />} />
            <Route
              path="/purchase-invoices"
              element={<Navigate to="/purchase-invoices/list" replace />}
            />

            {/* Credit Note Routes */}
            <Route path="/credit-notes/detail/:id" element={<CreditNoteDetailPage />} />
            <Route path="/credit-notes/new" element={<CreditNoteDetailPage />} />
            <Route path="/credit-notes/list" element={<CreditNotesPage />} />
            <Route path="/credit-notes" element={<Navigate to="/credit-notes/list" replace />} />

            {/* Debit Note Routes */}
            <Route path="/debit-notes/detail/:id" element={<DebitNoteDetailPage />} />
            <Route path="/debit-notes/new" element={<DebitNoteDetailPage />} />
            <Route path="/debit-notes/list" element={<DebitNotesPage />} />
            <Route path="/debit-notes" element={<Navigate to="/debit-notes/list" replace />} />

            {/* Offline Invoice Routes */}
            <Route path="/offline-invoices" element={<OfflineInvoicesPage />} />
            <Route path="/offline-invoices/list" element={<OfflineInvoiceListPage />} />
            <Route path="/offline-invoices/new" element={<OfflineInvoiceCreatePage />} />
            <Route path="/offline-invoices/edit/:id" element={<OfflineInvoiceCreatePage />} />

            {/* Day-End Closure Route */}
            <Route path="/day-end-closure" element={<DayEndClosurePage />} />
            
            {/* Admin Reporting Routes */}
            <Route path="/admin/reporting/parties" element={<PartiesReportPage />} />
            <Route path="/admin/reporting/customers" element={<CustomerMetricsPage />} />
            <Route path="/admin/reporting/sales" element={<SalesReportPage />} />

            {/* Tasks Routes */}
            <Route path="/tasks/detail/:id" element={<TaskDetailPage />} />
            <Route path="/tasks" element={<TaskListPage />} />

            </Route>
          </Route>

          {/* Client Manager Routes - Role Protected */}
          <Route element={<RoleProtectedRoute allowedRoles="client_manager" />}>
            <Route element={<ClientManagerLayout />}>
              <Route path="/client-manager/dashboard" element={<ClientManagerDashboardPage />} />
              <Route path="/client-manager/parties" element={<MyPartiesPage />} />
              <Route path="/client-manager/tasks" element={<TaskListPage />} />
            </Route>
          </Route>

          {/* Unauthorized Page - Accessible to all authenticated users */}
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

        </Route>

        {/* Fallback - redirect to login or dashboard based on auth */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      </NuqsAdapter>
    </BrowserRouter>
  )
}

export default AppRouter
